/**
 * Amazon URL parsing utilities
 * Handles various Amazon URL formats and extracts product IDs
 */

export interface AmazonUrlInfo {
  productId: string;
  urlType: "regular" | "short" | "unknown";
  originalUrl: string;
}

/**
 * Regular expressions for different Amazon URL patterns
 */
const AMAZON_URL_PATTERNS = {
  // Regular Amazon product URLs with /dp/ or /gp/product/
  regular: /amazon\.com\/.*?\/dp\/([A-Z0-9]{10})/i,

  // Short Amazon URLs (a.co, amzn.com)
  short: /(?:a\.co|amzn\.com)\/([a-zA-Z0-9]+)/i,

  // Amazon product URLs with different patterns
  alternative: /amazon\.com\/.*?\/([A-Z0-9]{10})/i,
};

/**
 * Checks if a string is an Amazon URL
 */
export function isAmazonUrl(url: string): boolean {
  const normalizedUrl = url.trim().toLowerCase();

  // Check for various Amazon domains
  const amazonDomains = ["amazon.com", "a.co", "amzn.com"];

  return amazonDomains.some((domain) => normalizedUrl.includes(domain));
}

/**
 * Extracts product ID from Amazon URL
 */
export function extractAmazonProductId(url: string): AmazonUrlInfo | null {
  const normalizedUrl = url.trim();

  // Try regular Amazon product URL pattern
  const regularMatch = normalizedUrl.match(AMAZON_URL_PATTERNS.regular);
  if (regularMatch) {
    return {
      productId: regularMatch[1],
      urlType: "regular",
      originalUrl: normalizedUrl,
    };
  }

  // Try alternative pattern as fallback
  const alternativeMatch = normalizedUrl.match(AMAZON_URL_PATTERNS.alternative);
  if (alternativeMatch) {
    return {
      productId: alternativeMatch[1],
      urlType: "regular",
      originalUrl: normalizedUrl,
    };
  }

  return null;
}

/**
 * Checks if a URL is a short Amazon URL
 */
export function isShortAmazonUrl(url: string): boolean {
  const normalizedUrl = url.trim().toLowerCase();
  const shortDomains = ["a.co", "amzn.com"];
  return shortDomains.some((domain) => normalizedUrl.includes(domain));
}

/**
 * Resolves short Amazon URLs to get the actual product URL
 * Note: This requires a backend service to work properly due to CORS restrictions
 */
export async function resolveShortAmazonUrl(
  _shortUrl: string,
): Promise<string | null> {
  try {
    // For now, return null since we can't reliably resolve short URLs from frontend
    // This would need a backend service to properly handle the redirect
    console.warn("Short Amazon URL resolution requires a backend service");
    return null;
  } catch (error) {
    console.error("Error resolving short Amazon URL:", error);
    return null;
  }
}

/**
 * Validates if a product ID looks like a valid Amazon product ID
 */
export function isValidAmazonProductId(productId: string): boolean {
  // Amazon product IDs are typically 10 characters long and contain letters and numbers
  return /^[A-Z0-9]{10}$/.test(productId);
}

/**
 * Normalizes Amazon URL to a standard format
 */
export function normalizeAmazonUrl(url: string): string {
  let normalized = url.trim();

  // Remove query parameters and fragments
  normalized = normalized.split("?")[0].split("#")[0];

  // Ensure protocol
  if (!normalized.startsWith("http")) {
    normalized = "https://" + normalized;
  }

  return normalized;
}

/**
 * Creates a clean Amazon product URL from product ID
 */
export function createAmazonProductUrl(
  productId: string,
  domain = "amazon.com",
): string {
  return `https://www.${domain}/dp/${productId}`;
}

/**
 * Extracts domain from Amazon URL
 */
export function extractAmazonDomain(_url: string) {
  return "amazon.com";
}
